import { useState } from "react";

import { useUsers } from "~/hooks";
import { MODAL_ROUTES, useNavigateModal } from "~/router";
import { usePermissions } from "~/screens/Admins/hooks/usePermissions";
import { User } from "~/shared.types";
import {
  parseApiErrorMessage,
  parseExistingUser,
  type ExistingUser,
} from "~/utils";
import type { EmailEditState } from "../components/EditableEmailRow";

export const useEditableEmail = (user: User | undefined) => {
  const navigateModal = useNavigateModal();
  const { canEditUserEmail } = usePermissions();
  const { updateUserEmailMutation } = useUsers();
  const { mutate: updateUserEmail, isPending: isSavingEmail } =
    updateUserEmailMutation;

  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [existingUser, setExistingUser] = useState<ExistingUser | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const closeEmailEdit = () => {
    setIsEditing(false);
    setError(undefined);
    setExistingUser(null);
    setPendingEmail(null);
  };

  const openExistingUser = (userId: string) => {
    closeEmailEdit();
    navigateModal(`${MODAL_ROUTES.userDetails}/${userId}`);
  };

  const emailEdit: EmailEditState = {
    isEditing,
    canEdit: canEditUserEmail,
    isSaving: isSavingEmail,
    error,
    existingUser,
    onOpenExistingUser: openExistingUser,
    onEdit: () => setIsEditing(true),
    onCancel: closeEmailEdit,
    onSave: (email) => {
      setError(undefined);
      setExistingUser(null);

      if (email === user?.email) {
        closeEmailEdit();
        return;
      }

      setPendingEmail(email);
    },
  };

  const confirmEmailChange = () => {
    if (!user || !pendingEmail) return;

    updateUserEmail(
      { userId: user.id, email: pendingEmail },
      {
        onSuccess: closeEmailEdit,
        onError: (requestError) => {
          setPendingEmail(null);
          setExistingUser(parseExistingUser(requestError));
          setError(parseApiErrorMessage(requestError));
        },
      },
    );
  };

  return {
    emailEdit,
    pendingEmail,
    isSavingEmail,
    confirmEmailChange,
    cancelEmailChange: () => setPendingEmail(null),
    closeEmailEdit,
  };
};
